/* SIZESOFTX
=========================================================================================
* Descripción: Modal de búsqueda de compras
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 14/05/2026 |                                   | Creación
* ---------------------------------------------------------------------------------------
*/

import TextField from "../../ui/TextField";
import React, { useState, useEffect, useRef } from "react";
import { Modal, Box, IconButton, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, InputAdornment } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import { getDateStringV2 } from "../../../settings/utils.js";
import { apiBuscarCompras } from "../../../settings/ApiConfig";
import { useRequest } from "../../../context/System/RequestContext";
import { useLayout } from "../../../context/System/LayoutContext";

const modalStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80vw',
    maxWidth: '1000px',
    height: '70vh',
    bgcolor: 'background.paper',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    borderRadius: '8px',
    p: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
};

export default function ModalBuscarCompras({
    openModal,
    handleCloseModal,
    onSelectCompra
}) {
    const { GetRequest } = useRequest();
    const { handleOpenAlert } = useLayout();

    const inputRef = useRef(null);
    const tablaRef = useRef(null);

    const persistRef = useRef({
        filtro: "",
        lista: [],
        indexSel: -1
    });

    const [filtro, setFiltro] = useState(persistRef.current.filtro);
    const [lista, setLista] = useState(persistRef.current.lista);
    const [indexSel, setIndexSel] = useState(persistRef.current.indexSel);

    useEffect(() => {
        persistRef.current = { filtro, lista, indexSel };
    }, [filtro, lista, indexSel]);

    useEffect(() => {
        if (openModal) {
            setFiltro(persistRef.current.filtro);
            setLista(persistRef.current.lista);
            setIndexSel(persistRef.current.indexSel);

            requestAnimationFrame(() => {
                if (persistRef.current.indexSel >= 0) {
                    tablaRef.current?.focus();
                    scrollToIndex(persistRef.current.indexSel);
                } else {
                    inputRef.current?.focus();
                    inputRef.current?.select();
                }
            });
        }
    }, [openModal]);

    const scrollToIndex = (index) => {
        const table = document.querySelector('.tabla-buscar-compras');
        if (table) {
            const rows = table.querySelectorAll('tbody tr');
            if (rows[index]) {
                rows[index].scrollIntoView({ block: 'center' });
            }
        }
    };

    const handleBuscar = async () => {
        if (!filtro || filtro.trim().length === 0) {
            handleOpenAlert("Ingrese un filtro de búsqueda", "warning");
            return;
        }

        try {
            const url = apiBuscarCompras.replace("{busqueda}", encodeURIComponent(filtro.trim()));
            const data = await GetRequest({ url, loader: "Buscando compras..." });

            if (data?.hasError) {
                handleOpenAlert(data.message || "Error al buscar compras", "error");
                setLista([]);
                setIndexSel(-1);
                return;
            }

            const compras = Array.isArray(data?.result) ? data.result : [];
            setLista(compras);
            setIndexSel(compras.length > 0 ? 0 : -1);
        } catch (error) {
            console.error("Error buscando compras:", error);
            handleOpenAlert("Error al buscar compras", "error");
            setLista([]);
            setIndexSel(-1);
        }
    };

    const handleKeyDownGlobal = (event) => {
        if (event.altKey && event.key.trim().toUpperCase() === "F1") {
            event.preventDefault();
            event.stopPropagation();
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    };

    const handleKeyDown = async (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            if (lista.length > 0 && indexSel >= 0) {
                const cargada = await onSelectCompra(lista[indexSel]);
                if (cargada !== false) handleClose();
            } else {
                handleBuscar();
            }
            return;
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            if (lista.length > 0) {
                const newIndex = indexSel < lista.length - 1 ? indexSel + 1 : 0;
                setIndexSel(newIndex);
                scrollToIndex(newIndex);
            }
            return;
        }

        if (event.key === "ArrowUp") {
            event.preventDefault();
            if (lista.length > 0) {
                const newIndex = indexSel > 0 ? indexSel - 1 : lista.length - 1;
                setIndexSel(newIndex);
                scrollToIndex(newIndex);
            }
            return;
        }

        if (event.key === "Escape") {
            event.preventDefault();
            handleClose();
            return;
        }
    };

    const handleClose = () => {
        persistRef.current = { filtro, lista, indexSel };
        handleCloseModal();
    };

    const handleRowClick = async (compra, index) => {
        setIndexSel(index);
        const cargada = await onSelectCompra(compra);
        if (cargada !== false) handleClose();
    };

    useEffect(() => {
        if (openModal) {
            window.addEventListener("keydown", handleKeyDownGlobal);
            return () => window.removeEventListener("keydown", handleKeyDownGlobal);
        }
    }, [openModal]);

    if (!openModal) return null;

    return (
        <Modal open={openModal} onClose={handleClose}>
            <Box sx={modalStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid #ddd' }}>
                    <Typography variant="h6" component="div">
                        Buscar Compras
                    </Typography>
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ p: 2, borderBottom: '1px solid #ddd' }}>
                    <TextField autoComplete="off"
                        inputRef={inputRef}
                        name="filtro"
                        label="Filtro (Folio, Proveedor, Factura):"
                        value={filtro}
                        onChange={(e) => setFiltro(e.target.value)}
                        onKeyDown={handleKeyDown}
                        fullWidth
                        size="small"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleBuscar} size="small">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        autoFocus
                    />
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                    <TableContainer component={Paper} sx={{ maxHeight: '100%' }}>
                        <Table stickyHeader size="small" className="tabla-buscar-compras">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '100px' }}>Folio</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '90px' }}>Fecha</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '100px' }}>PC</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '150px' }}>Proveedor</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '120px' }}>Factura/Remisión</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '100px' }}>Tipo Pago</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', width: '100px' }}>Status</TableCell>
                                    <TableCell sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', textAlign: 'right' }}>Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lista.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#666' }}>
                                            {filtro ? "Sin resultados" : "Ingrese un filtro y presione Enter"}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    lista.map((compra, index) => (
                                        <TableRow
                                            key={`${compra.foliocompra}-${compra.pc}`}
                                            onClick={() => handleRowClick(compra, index)}
                                            sx={{
                                                cursor: 'pointer',
                                                backgroundColor: index === indexSel ? '#e3f2fd' : 'inherit',
                                                '&:hover': { backgroundColor: '#f5f5f5' }
                                            }}
                                        >
                                            <TableCell>{compra.foliocompra}</TableCell>
                                            <TableCell>{compra.fecharegistro ? getDateStringV2(compra.fecharegistro) : ""}</TableCell>
                                            <TableCell>{compra.pc}</TableCell>
                                            <TableCell>{compra.razonsocial || compra.nombreproveedor || ""}</TableCell>
                                            <TableCell>{compra.facturaremision || ""}</TableCell>
                                            <TableCell>{compra.tipopago || "CONTADO"}</TableCell>
                                            <TableCell>{compra.statuscompra || "AC"}</TableCell>
                                            <TableCell sx={{ textAlign: 'right' }}>{parseFloat(compra.totalcompra || 0).toFixed(2)}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                <Box sx={{ p: 1, borderTop: '1px solid #ddd', backgroundColor: '#f9f9f9' }}>
                    <Typography variant="body2" color="textSecondary">
                        {lista.length} registro(s) encontrado(s). Use flechas ↑↓ para navegar, Enter para seleccionar, Esc para cerrar.
                    </Typography>
                </Box>
            </Box>
        </Modal>
    );
}